import {
  executeCessionBatch,
  getAECsInPosession,
  getCessionBatchStatus,
} from 'lib/api/reSell/factoring';
import { showAlert } from 'lib/functions/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  CessionDocument,
  CessionDocumentPayload,
  CessionType,
  FundCessionRequest,
  FundInfo,
  ResellSuggestedDocument,
} from '../../types';

export interface BatchItem {
  id: string;
  documentId: string;
  folio: string;
  businessIdentifier: string;
  stakeholderIdentifier?: string;
  status:
    | 'PENDING'
    | 'LOADING_XML'
    | 'XML_LOADED'
    | 'EXECUTING'
    | 'SUCCESS'
    | 'FAILED';
  message?: string;
}

export interface BatchStatus {
  batchId: string;
  status:
    | 'PENDING'
    | 'PROCESSING'
    | 'XMLS_LOADED'
    | 'PROCESSING_CESSIONS'
    | 'COMPLETED'
    | 'PARTIALLY_COMPLETED'
    | 'FAILED';
  cessionType: string;
  canCede: boolean;
  progress: {
    total: number;
    processed: number;
    successful: number;
    failed: number;
    percentage: number;
  };
  startedAt?: string;
  completedAt?: string | null;
  errorMessage?: string | null;
  items: BatchItem[];
}

export type CessionPhase =
  | 'loading-xmls'
  | 'ready-to-cede'
  | 'executing-cessions'
  | 'completed';

interface UseCessionBatchPollingProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  documents: CessionDocument[];
  bankAccountId: number;
  funds: FundInfo[];
  amountToReceiveInput?: number;
  setUpMainPage: () => Promise<void>;
  resellsToApply: ResellSuggestedDocument[];
}

interface UseCessionBatchPollingReturn {
  batchStatus: BatchStatus | null;
  initialLoading: boolean;
  executingCessions: boolean;
  phase: CessionPhase;
  failedStep: number | null;
  errorMessage: string | null;
  progress: BatchStatus['progress'];
  tableData: Array<{ folio: string; status: string; message: string }>;
  activeStep: number;
  isCompleted: boolean;
  handleClose: () => Promise<void>;
  handleFinish: () => Promise<void>;
}

const POLLING_INTERVAL = 3000; // 3 segundos

export const useCessionBatchPolling = ({
  open,
  setOpen,
  documents,
  bankAccountId,
  funds,
  amountToReceiveInput,
  setUpMainPage,
  resellsToApply,
}: UseCessionBatchPollingProps): UseCessionBatchPollingReturn => {
  const dispatch = useDispatch();

  // Estados principales
  const [batchId, setBatchId] = useState<string | null>(null);
  const [batchStatus, setBatchStatus] = useState<BatchStatus | null>(null);
  const [initialLoading, setInitialLoading] = useState(false);
  const [executingCessions, setExecutingCessions] = useState(false);
  const [phase, setPhase] = useState<CessionPhase>('loading-xmls');
  const [failedStep, setFailedStep] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [documentsWithStatus, setDocumentsWithStatus] = useState<
    CessionDocument[]
  >([]);

  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const pollBatchStatus = useCallback(
    async (id: string) => {
      try {
        const status = await getCessionBatchStatus(dispatch, id);
        setBatchStatus(status);

        if (status?.items) {
          setDocumentsWithStatus(() => {
            return documents.map((doc: CessionDocument) => {
              const item = status.items.find(
                (i: BatchItem) => i.documentId === doc.id,
              );
              const fund = funds.find((f) => f.id === doc.fundId);
              return {
                ...doc,
                fundName: fund?.name,
                status:
                  item?.status === 'SUCCESS' || item?.status === 'XML_LOADED',
                message: item?.message,
                stakeholderIdentifier:
                  item?.stakeholderIdentifier || doc.stakeholderIdentifier,
              };
            });
          });
        }

        setPhase((currentPhase) => {
          if (status?.status === 'FAILED') {
            stopPolling();
            const backendErrorMessage = status?.errorMessage || null;

            if (
              currentPhase === 'loading-xmls' ||
              currentPhase === 'ready-to-cede'
            ) {
              setFailedStep(0);
              setErrorMessage(
                backendErrorMessage
                  ? `Error al procesar: ${backendErrorMessage}`
                  : 'Error al cargar los XMLs de los documentos',
              );
              return currentPhase;
            } else if (currentPhase === 'executing-cessions') {
              setFailedStep(1);
              setErrorMessage(
                backendErrorMessage
                  ? `Error al procesar: ${backendErrorMessage}`
                  : 'Error al ejecutar las cesiones',
              );
              return currentPhase;
            }
            return 'completed';
          }

          if (
            status?.status === 'XMLS_LOADED' &&
            status?.canCede &&
            currentPhase === 'loading-xmls'
          ) {
            setFailedStep(null);
            setErrorMessage(null);
            return 'ready-to-cede';
          }

          if (
            status?.status === 'XMLS_LOADED' &&
            !status?.canCede &&
            currentPhase === 'loading-xmls'
          ) {
            stopPolling();
            const failedCount =
              status?.items?.filter((i: BatchItem) => i.status === 'FAILED')
                .length || 0;
            setFailedStep(0);
            setErrorMessage(
              `${failedCount} documento${failedCount > 1 ? 's' : ''} no pudieron ser procesados. Revise los errores en la tabla.`,
            );
            return currentPhase;
          }

          // PENDING o PROCESSING: continuar en fase de carga
          if (
            (status?.status === 'PENDING' || status?.status === 'PROCESSING') &&
            currentPhase === 'loading-xmls'
          ) {
            return currentPhase;
          }

          // PROCESSING_CESSIONS: continuar en fase de ejecución
          if (
            status?.status === 'PROCESSING_CESSIONS' &&
            currentPhase === 'executing-cessions'
          ) {
            return currentPhase;
          }

          // COMPLETED o PARTIALLY_COMPLETED: proceso terminado
          if (
            status?.status === 'COMPLETED' ||
            status?.status === 'PARTIALLY_COMPLETED'
          ) {
            stopPolling();
            setExecutingCessions(false);
            return 'completed';
          }

          return currentPhase;
        });
      } catch (error) {
        console.error('Error al obtener estado del batch:', error);
        stopPolling();
        setPhase((currentPhase) => {
          if (
            currentPhase === 'loading-xmls' ||
            currentPhase === 'ready-to-cede'
          ) {
            setFailedStep(0);
            setErrorMessage('Error al obtener el estado del batch');
            return currentPhase;
          } else if (currentPhase === 'executing-cessions') {
            setFailedStep(1);
            setErrorMessage('Error al obtener el estado del batch');
            return currentPhase;
          }
          return 'completed';
        });
      }
    },
    [dispatch, documents, funds, stopPolling],
  );

  const startPolling = useCallback(
    (id: string) => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      pollingIntervalRef.current = setInterval(() => {
        void pollBatchStatus(id);
      }, POLLING_INTERVAL);
    },
    [pollBatchStatus],
  );

  const createBatch = useCallback(async () => {
    if (!open || documents.length === 0) return;

    setInitialLoading(true);
    setPhase('loading-xmls');

    try {
      const documentForResell = documents.map((doc) => ({
        id: doc.id,
        businessIdentifier: doc.businessIdentifier,
        folio: doc.folio,
        cessionDate: doc.cessionDate ? new Date(doc.cessionDate) : new Date(),
        code: doc.subType || doc.docType || '33',
      }));

      const response = await getAECsInPosession(dispatch, documentForResell, {
        usePolling: true,
        cessionType: CessionType.FUND,
      });

      if (response?.batchId) {
        setBatchId(response.batchId);
        void pollBatchStatus(response.batchId);
      } else {
        setFailedStep(0);
        setErrorMessage('Error al crear el batch de cesiones');
        setPhase('completed');
      }
    } catch (error) {
      console.error('Error al crear batch:', error);
      setFailedStep(0);
      setErrorMessage('Error al crear el batch de cesiones');
      setPhase('completed');
    } finally {
      setInitialLoading(false);
    }
  }, [dispatch, documents, open, pollBatchStatus]);

  const handleExecuteCessions = useCallback(async () => {
    if (!batchId || !batchStatus) return;

    if (!batchStatus.canCede || batchStatus.status !== 'XMLS_LOADED') {
      console.error(
        'No se pueden ejecutar cesiones: XMLs no están completamente cargados',
      );
      return;
    }

    setExecutingCessions(true);
    setPhase('executing-cessions');

    try {
      const resellsToApplyPayload = resellsToApply.map(
        (resell: ResellSuggestedDocument) => ({
          resellId: resell.id,
          amount: Number(resell.amountToConciliate || 0),
        }),
      );

      const payload: FundCessionRequest = {
        type: CessionType.FUND,
        amountToReceive: amountToReceiveInput,
        resellsToApply: resellsToApplyPayload,
        documents: documentsWithStatus.map(
          (document: CessionDocument): CessionDocumentPayload => ({
            id: document.id,
            folio: document.folio,
            businessIdentifier: document.businessIdentifier,
            stakeholderIdentifier:
              document.stakeholderIdentifier ?? document.businessIdentifier,
            amount: document.amount ?? 0,
            originalAmount: document.originalAmount,
            advance: document.advance,
            arrearRate: document.arrearRate,
            subType: document.subType ?? document.docType ?? '33',
            bankAccountId: bankAccountId,
            cessionDate: document.cessionDate
              ? new Date(document.cessionDate)
              : new Date(),
            startDate: new Date(document.startDate),
            endDate: new Date(document.endDate),
            rate: document.rate?.toString() ?? '',
            rateCost: document.rateCost?.toString() ?? '',
            operationCostFixed: document.operationCostFixed?.toString(),
            operationCostVariable: document.operationCostVariable?.toString(),
            fund: {
              id: document.fundId ?? '',
              name: document.fundName ?? '',
            },
          }),
        ),
      };

      await executeCessionBatch(dispatch, batchId, payload);
    } catch (error) {
      console.error('Error al ejecutar cesiones:', error);
      showAlert(dispatch, 'error', 'Error al ejecutar las cesiones');
      setExecutingCessions(false);
      setPhase('ready-to-cede');
    }
  }, [
    batchId,
    batchStatus,
    resellsToApply,
    amountToReceiveInput,
    documentsWithStatus,
    bankAccountId,
    dispatch,
  ]);

  const handleClose = useCallback(async () => {
    stopPolling();
    setOpen(false);
  }, [stopPolling, setOpen]);

  const handleFinish = useCallback(async () => {
    stopPolling();
    await setUpMainPage();
    setOpen(false);
  }, [stopPolling, setUpMainPage, setOpen]);

  const resetState = useCallback(() => {
    stopPolling();
    setBatchId(null);
    setBatchStatus(null);
    setPhase('loading-xmls');
    setDocumentsWithStatus([]);
    setFailedStep(null);
    setErrorMessage(null);
    setExecutingCessions(false);
  }, [stopPolling]);

  useEffect(() => {
    if (open) {
      void createBatch();
    } else {
      resetState();
    }

    return () => {
      stopPolling();
    };
  }, [open, createBatch, resetState, stopPolling]);

  useEffect(() => {
    if (
      batchStatus?.status === 'COMPLETED' ||
      batchStatus?.status === 'PARTIALLY_COMPLETED' ||
      batchStatus?.status === 'FAILED'
    ) {
      stopPolling();
      return;
    }

    if (
      batchId &&
      (phase === 'loading-xmls' ||
        phase === 'ready-to-cede' ||
        phase === 'executing-cessions')
    ) {
      startPolling(batchId);
    }

    return () => {
      if (
        batchStatus?.status === 'COMPLETED' ||
        batchStatus?.status === 'PARTIALLY_COMPLETED' ||
        batchStatus?.status === 'FAILED'
      ) {
        stopPolling();
      }
    };
  }, [batchId, phase, batchStatus?.status, startPolling, stopPolling]);

  useEffect(() => {
    if (
      phase === 'ready-to-cede' &&
      batchId &&
      batchStatus &&
      !executingCessions
    ) {
      void handleExecuteCessions();
    }
  }, [phase, batchId, batchStatus, executingCessions, handleExecuteCessions]);

  const progress = useMemo(
    () =>
      batchStatus?.progress || {
        total: documents.length,
        processed: 0,
        successful: 0,
        failed: 0,
        percentage: 0,
      },
    [batchStatus?.progress, documents.length],
  );

  const tableData = useMemo(() => {
    if (!batchStatus?.items) return [];
    const statusMap: Record<BatchItem['status'], string> = {
      PENDING: 'Pendiente',
      LOADING_XML: 'Cargando XML',
      XML_LOADED: 'Aceptada',
      EXECUTING: 'Ejecutando cesiones',
      SUCCESS: 'Cesiones ejecutadas',
      FAILED: 'Error al ejecutar cesiones',
    };
    return batchStatus.items.map((item) => ({
      folio: item.folio,
      status: statusMap[item.status] || 'Pendiente',
      message: item.message || '-',
    }));
  }, [batchStatus?.items]);

  const isCompleted =
    batchStatus?.status === 'COMPLETED' ||
    batchStatus?.status === 'PARTIALLY_COMPLETED';

  const activeStep = useMemo(() => {
    if (failedStep !== null) {
      return failedStep;
    }
    if (phase === 'completed' && isCompleted) {
      return 2;
    }
    if (
      phase === 'ready-to-cede' ||
      phase === 'executing-cessions' ||
      (phase === 'completed' && !isCompleted)
    ) {
      return 1;
    }
    return 0;
  }, [phase, isCompleted, failedStep]);

  return {
    batchStatus,
    initialLoading,
    executingCessions,
    phase,
    failedStep,
    errorMessage,
    progress,
    tableData,
    activeStep,
    isCompleted,
    handleClose,
    handleFinish,
  };
};
